import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import colors from "../../../styles/colors";
import { apiListWrestler } from "../../../api/wrestlerApi";
import type { Wrestler, Belt } from "../../../types";
import type { AddChampionPayload } from "../../../api/beltApi";

const Container = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
`;

const ModalDialog = styled.div`
    position: relative;
    width: 100%;
    max-width: 600px;
    margin: 28px auto;
    pointer-events: none;
    max-height: 90vh;
    overflow-y: auto;
`;

const ModalContent = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    pointer-events: auto;
    background-color: ${colors.bgColorBlack2};
    color: white;
    background-clip: padding-box;
    border: 5px solid gold;
    border-radius: 5px;
    outline: 0;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #dee2e6;
`;

const ModalTitle = styled.h5`
    margin: 0;
    line-height: 1.5;
    font-size: 20px;
    font-weight: 500;
    color: gold;
`;

const CloseButton = styled.button`
    box-sizing: content-box;
    width: 1em;
    height: 1em;
    padding: 4px;
    color: white;
    border: 0;
    border-radius: 4px;
    opacity: 0.5;
    background: transparent;
    cursor: pointer;
    font-size: 24px;
    font-weight: 700;
    line-height: 1;

    &:hover {
        opacity: 0.75;
    }

    &:before {
        content: "×";
    }
`;

const ModalBody = styled.div`
    position: relative;
    flex: 1 1 auto;
    padding: 16px;
`;

const ModalFooter = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
    padding: 12px;
    border-top: 1px solid #dee2e6;
    gap: 8px;
`;

const FormGroup = styled.div`
    margin-bottom: 16px;
`;

const FormLabel = styled.label`
    display: inline-block;
    margin-bottom: 8px;
    font-weight: 500;
    color: gold;
`;

const FormInput = styled.input`
    display: block;
    width: 100%;
    padding: 6px 12px;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    color: #000000;
    background-color: #ffffff;
    border: 1px solid #d3d3d3;
    border-radius: 4px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
        border-color: #86b7fe;
        outline: 0;
        box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.25);
    }
`;

const FormCheckbox = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 4px;

    & input[type="checkbox"] {
        width: 20px;
        height: 20px;
        cursor: pointer;
    }

    & label {
        cursor: pointer;
        color: white;
        font-size: 16px;
    }
`;

const Button = styled.button`
    display: inline-block;
    font-weight: 400;
    line-height: 1.5;
    color: #ffffff;
    text-align: center;
    cursor: pointer;
    user-select: none;
    background-color: transparent;
    border: 1px solid transparent;
    padding: 6px 12px;
    font-size: 16px;
    border-radius: 4px;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;

    &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }
`;

const ButtonPrimary = styled(Button)`
    color: white;
    background-color: #0d6efd;
    border-color: #0d6efd;

    &:hover:not(:disabled) {
        background-color: #0b5ed7;
        border-color: #0a58ca;
    }
`;

const ButtonSecondary = styled(Button)`
    color: #ffffff;
    background-color: #6c757d;
    border-color: #6c757d;

    &:hover:not(:disabled) {
        background-color: #5c636a;
        border-color: #565e64;
    }
`;

const ErrorText = styled.p`
    margin-top: 6px;
    margin-bottom: 0;
    color: #dc3545;
    font-size: 14px;
`;

const LoadingText = styled.p`
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
    padding: 20px;
`;

const InfoText = styled.p`
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 4px;
`;

const SelectedWrestlersList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const SelectedWrestlerTag = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background-color: gold;
    color: ${colors.bgColorBlack};
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;

    button {
        background: none;
        border: none;
        color: ${colors.bgColorBlack};
        cursor: pointer;
        font-size: 16px;
        font-weight: bold;
        padding: 0;
        line-height: 1;

        &:hover {
            color: #dc3545;
        }
    }
`;

const AutocompleteContainer = styled.div`
    position: relative;
    width: 100%;
`;

const AutocompleteInput = styled.input`
    display: block;
    width: 100%;
    padding: 10px 12px;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    color: #000000;
    background-color: #ffffff;
    border: 1px solid #d3d3d3;
    border-radius: 4px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
        border-color: #86b7fe;
        outline: 0;
        box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.25);
    }

    &::placeholder {
        color: #6c757d;
    }
`;

const AutocompleteDropdown = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;
    background-color: #ffffff;
    border: 1px solid #d3d3d3;
    border-top: none;
    border-radius: 0 0 4px 4px;
    z-index: 1000;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
`;

const AutocompleteItem = styled.li<{ $isHighlighted?: boolean }>`
    padding: 10px 12px;
    cursor: pointer;
    color: #000000;
    background-color: ${props => props.$isHighlighted ? '#e9ecef' : '#ffffff'};

    &:hover {
        background-color: #e9ecef;
    }
`;

const NoResultsItem = styled.li`
    padding: 10px 12px;
    color: #6c757d;
    font-style: italic;
`;

interface AddChampionModaleProps {
    belt: Belt;
    onClose: () => void;
    onSave: (data: AddChampionPayload) => Promise<void>;
}

function AddChampionModale({ belt, onClose, onSave }: AddChampionModaleProps) {
    const [wrestlers, setWrestlers] = useState<Wrestler[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [isVacant, setIsVacant] = useState(false);
    const [date, setDate] = useState<string>("");
    const [showName, setShowName] = useState<string>("");
    const [selectedWrestlerIds, setSelectedWrestlerIds] = useState<number[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const autocompleteRef = useRef<HTMLDivElement>(null);

    const isTagTeam = belt.is_team || belt.is_tag;

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const wrestlersRes = await apiListWrestler();
                const wrestlerData = Array.isArray(wrestlersRes) ? wrestlersRes : [];
                setWrestlers(wrestlerData.sort((a, b) => a.name.localeCompare(b.name)));
            } catch (error) {
                console.error("Errore nel caricamento dei dati:", error);
                setErrors({ general: "Errore nel caricamento dei dati" });
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    // Chiudi dropdown quando si clicca fuori
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const availableWrestlers = wrestlers
        .filter(w => !selectedWrestlerIds.includes(w.id))
        .filter(w => searchTerm === "" || w.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const handleSelectWrestler = (wrestler: Wrestler) => {
        if (selectedWrestlerIds.includes(wrestler.id)) {
            setErrors({ ...errors, wrestlers: "Wrestler già selezionato" });
            return;
        }

        setSelectedWrestlerIds([...selectedWrestlerIds, wrestler.id]);
        setSearchTerm("");
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        setErrors({ ...errors, wrestlers: "" });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isDropdownOpen || availableWrestlers.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex(prev =>
                prev < availableWrestlers.length - 1 ? prev + 1 : prev
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < availableWrestlers.length) {
                handleSelectWrestler(availableWrestlers[highlightedIndex]);
            }
        } else if (e.key === "Escape") {
            setIsDropdownOpen(false);
        }
    };

    const handleRemoveWrestler = (id: number) => {
        setSelectedWrestlerIds(selectedWrestlerIds.filter(wId => wId !== id));
    };

    const getWrestlerName = (id: number): string => {
        const wrestler = wrestlers.find(w => w.id === id);
        return wrestler?.name || "Sconosciuto";
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});

        const nextErrors: Record<string, string> = {};

        if (!date) {
            nextErrors.date = "Inserisci la data";
        }

        if (!showName.trim()) {
            nextErrors.showName = "Inserisci il nome dello show";
        }

        if (!isVacant && selectedWrestlerIds.length === 0) {
            nextErrors.wrestlers = "Seleziona almeno un wrestler";
        }

        if (!isVacant && !isTagTeam && selectedWrestlerIds.length > 1) {
            nextErrors.wrestlers = "Per cinture singole puoi selezionare solo un wrestler";
        }

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            return;
        }

        const payload: AddChampionPayload = {
            belt_id: belt.id,
            is_vacant: isVacant,
            date: date,
            show_name: showName.trim(),
            wrestler_ids: isVacant ? [] : selectedWrestlerIds
        };

        setIsSaving(true);
        try {
            await onSave(payload);
            onClose();
        } catch (err: any) {
            setErrors({ general: err?.message || "Errore durante il salvataggio" });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Container className="add-champion-modale-container">
            <ModalDialog className="add-champion-modale-dialog">
                <ModalContent className="add-champion-modale-content">
                    <form onSubmit={handleSubmit}>
                        <ModalHeader className="add-champion-modale-header">
                            <ModalTitle className="add-champion-modale-title">
                                Aggiungi Campione - {belt.name}
                            </ModalTitle>
                            <CloseButton
                                type="button"
                                aria-label="Close"
                                onClick={onClose}
                            />
                        </ModalHeader>
                        <ModalBody className="add-champion-modale-body">
                            {isLoading ? (
                                <LoadingText>Caricamento dati...</LoadingText>
                            ) : (
                                <>
                                    <FormGroup>
                                        <FormCheckbox>
                                            <input
                                                id="isVacant"
                                                type="checkbox"
                                                checked={isVacant}
                                                onChange={(e) => {
                                                    setIsVacant(e.target.checked);
                                                    if (e.target.checked) {
                                                        setSelectedWrestlerIds([]);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="isVacant">
                                                Rendi vacante questa cintura
                                            </label>
                                        </FormCheckbox>
                                        <InfoText>
                                            Seleziona se la cintura viene resa vacante (nessun vincitore)
                                        </InfoText>
                                    </FormGroup>

                                    <FormGroup>
                                        <FormLabel htmlFor="date">
                                            Data *
                                        </FormLabel>
                                        <FormInput
                                            id="date"
                                            type="date"
                                            value={date}
                                            onChange={(e) => setDate(e.target.value)}
                                        />
                                        {errors.date && <ErrorText>{errors.date}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <FormLabel htmlFor="showName">
                                            Nome Show *
                                        </FormLabel>
                                        <FormInput
                                            id="showName"
                                            type="text"
                                            value={showName}
                                            onChange={(e) => setShowName(e.target.value)}
                                            placeholder="Es: Fear, Tap Out, Forumania..."
                                        />
                                        <InfoText>
                                            Inserisci il nome dello show dove è stato vinto il titolo
                                        </InfoText>
                                        {errors.showName && <ErrorText>{errors.showName}</ErrorText>}
                                    </FormGroup>

                                    {!isVacant && (
                                        <FormGroup>
                                            <FormLabel>
                                                Wrestler{isTagTeam ? "s" : ""} *
                                            </FormLabel>
                                            <AutocompleteContainer ref={autocompleteRef}>
                                                <AutocompleteInput
                                                    type="text"
                                                    value={searchTerm}
                                                    onChange={(e) => {
                                                        setSearchTerm(e.target.value);
                                                        setIsDropdownOpen(true);
                                                        setHighlightedIndex(-1);
                                                    }}
                                                    onFocus={() => setIsDropdownOpen(true)}
                                                    onKeyDown={handleKeyDown}
                                                    placeholder="Digita per cercare wrestler..."
                                                />
                                                {isDropdownOpen && searchTerm.length > 0 && (
                                                    <AutocompleteDropdown>
                                                        {availableWrestlers.length > 0 ? (
                                                            availableWrestlers.slice(0, 10).map((wrestler, index) => (
                                                                <AutocompleteItem
                                                                    key={wrestler.id}
                                                                    $isHighlighted={index === highlightedIndex}
                                                                    onClick={() => handleSelectWrestler(wrestler)}
                                                                >
                                                                    {wrestler.name}
                                                                </AutocompleteItem>
                                                            ))
                                                        ) : (
                                                            <NoResultsItem>Nessun wrestler trovato</NoResultsItem>
                                                        )}
                                                    </AutocompleteDropdown>
                                                )}
                                            </AutocompleteContainer>
                                            <InfoText>
                                                {isTagTeam
                                                    ? "Per le cinture tag team puoi aggiungere più wrestler"
                                                    : "Seleziona il wrestler che ha vinto il titolo"}
                                            </InfoText>
                                            {errors.wrestlers && <ErrorText>{errors.wrestlers}</ErrorText>}

                                            {selectedWrestlerIds.length > 0 && (
                                                <SelectedWrestlersList>
                                                    {selectedWrestlerIds.map((id) => (
                                                        <SelectedWrestlerTag key={id}>
                                                            {getWrestlerName(id)}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveWrestler(id)}
                                                                title="Rimuovi"
                                                            >
                                                                ×
                                                            </button>
                                                        </SelectedWrestlerTag>
                                                    ))}
                                                </SelectedWrestlersList>
                                            )}
                                        </FormGroup>
                                    )}

                                    {errors.general && <ErrorText>{errors.general}</ErrorText>}
                                </>
                            )}
                        </ModalBody>
                        <ModalFooter className="add-champion-modale-footer">
                            <ButtonSecondary
                                type="button"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                Annulla
                            </ButtonSecondary>
                            <ButtonPrimary
                                type="submit"
                                disabled={isSaving || isLoading}
                            >
                                {isSaving ? "Salvataggio..." : (isVacant ? "Rendi Vacante" : "Aggiungi Campione")}
                            </ButtonPrimary>
                        </ModalFooter>
                    </form>
                </ModalContent>
            </ModalDialog>
        </Container>
    );
}

export default AddChampionModale;
